import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { useState } from "react";

/**
 * Interactive-gallery heart overlay (Phase G2).
 *
 * Gold-filled when the current collaborator has favorited the photo, cream
 * outline otherwise. A small charcoal count badge appears only when ≥1
 * collaborator has hearted (count > 0); hovering the badge reveals the
 * display-names of who picked it.
 *
 * Motion: 150ms scale bounce on toggle, opacity/transform only, respects
 * `prefers-reduced-motion`. No red, ever.
 *
 *   isMine            — bool, fills the heart
 *   count             — int, total picks across all collaborators
 *   collaborators     — [{ display_name, role }]
 *   onToggle          — () => void
 *   size              — "sm" | "md"  (md = grid/template, sm = lightbox uses
 *                       its own bigger pill — this is just for thumbs)
 */
export default function InteractiveHeart({
  isMine, count = 0, collaborators = [], onToggle, size = "md", testid,
}) {
  const [hoverBadge, setHoverBadge] = useState(false);
  const dim = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const iconDim = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <div className="relative inline-flex items-center gap-1.5">
      <motion.button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
        whileTap={{ scale: 0.86 }}
        animate={{ scale: isMine ? [1, 1.18, 1] : 1 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        aria-pressed={!!isMine}
        aria-label={isMine ? "Unfavorite" : "Favorite"}
        data-testid={testid || "interactive-heart-btn"}
        className={[
          "rounded-full inline-flex items-center justify-center backdrop-blur-sm transition-colors",
          dim,
          isMine
            ? "bg-[#C9A961] text-stone-900 shadow-sm"
            : "bg-stone-900/30 text-[#F5EFE0] hover:bg-stone-900/55 border border-[#F5EFE0]/60",
        ].join(" ")}
      >
        <Heart
          className={iconDim}
          strokeWidth={isMine ? 0 : 1.5}
          fill={isMine ? "currentColor" : "none"}
        />
      </motion.button>

      {count > 0 && (
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setHoverBadge(true)}
          onMouseLeave={() => setHoverBadge(false)}
          className="relative"
          data-testid="interactive-heart-count"
        >
          <span className="inline-flex items-center px-1.5 py-0.5 bg-stone-900/85 text-[10px] tracking-[0.08em] text-[#F5EFE0] rounded-sm">
            <span className="text-[#C9A961] mr-0.5">♥</span>{count}
          </span>
          {hoverBadge && collaborators.length > 0 && (
            <div
              className="absolute z-30 left-1/2 -translate-x-1/2 mt-1 top-full bg-stone-900 text-[#F5EFE0] text-[11px] px-2.5 py-1.5 whitespace-nowrap rounded-sm shadow-lg pointer-events-none"
              data-testid="interactive-heart-tooltip"
            >
              <div className="text-[9px] tracking-[0.22em] uppercase text-[#C9A961] mb-0.5">Picked by</div>
              {collaborators.map((c, i) => (
                <div key={`${c.display_name}-${i}`} className="font-light">
                  {c.display_name}
                  {c.role === "primary" && <span className="ml-1 text-[9px] tracking-[0.18em] uppercase text-[#C9A961]">· primary</span>}
                </div>
              ))}
            </div>
          )}
        </button>
      )}
    </div>
  );
}
